import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { Nav } from '../components/sections/Nav';
import { Footer } from '../components/sections/Footer';
import { useLenis } from '../lib/useLenis';

const SENTRIC_SCRIPT_SRC = 'https://api.sentricai.io/js/form_embed.js';

const Estimate = () => {
    useLenis();
    const navigate = useNavigate();

    useEffect(() => {
        const prev = document.title;
        document.title = 'Get a Free Estimate | MSD Painting Services';
        return () => { document.title = prev; };
    }, []);

    // Load the Sentric (GoHighLevel) form embed script (handles iframe auto-resize)
    useEffect(() => {
        if (!document.querySelector(`script[src="${SENTRIC_SCRIPT_SRC}"]`)) {
            const s = document.createElement('script');
            s.src = SENTRIC_SCRIPT_SRC;
            s.async = true;
            document.body.appendChild(s);
        }
    }, []);

    // Best-effort: when the embedded form reports a submission, move the prospect to the
    // dedicated thank-you view. (For a guaranteed redirect, also set the form's
    // "On Submit → Redirect URL" in GoHighLevel to /estimate/thank-you.)
    useEffect(() => {
        const onMessage = (e) => {
            if (!/sentricai\.io|leadconnectorhq\.com|gohighlevel/i.test(e.origin || '')) return;
            let data = e.data;
            try { if (typeof data === 'string') data = JSON.parse(data); } catch { /* non-JSON message */ }
            const blob = JSON.stringify(data || '');
            if (/submit|submitted|success|thank/i.test(blob) && !/height|resize|scroll/i.test(blob)) {
                // Mark that a genuine lead submission just occurred, so the thank-you
                // page fires the Google Ads conversion exactly once (see ThankYou.jsx).
                try { sessionStorage.setItem('msd:leadConversion', 'pending'); } catch { /* storage blocked */ }
                navigate('/estimate/thank-you');
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [navigate]);

    return (
        <div className="relative min-h-screen bg-ink-900 text-bone-100" data-testid="estimate-page">
            <Nav />

            {/* Hero header */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-30"
                    style={{ background: 'radial-gradient(ellipse at 15% 30%, rgba(42,107,242,0.20) 0%, rgba(42,107,242,0) 55%), radial-gradient(ellipse at 85% 70%, rgba(230,57,70,0.18) 0%, rgba(230,57,70,0) 55%)' }}
                />
                <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 pt-32 md:pt-40 pb-14 md:pb-20">
                    <div className="mb-6 flex items-center gap-4">
                        <span className="w-10 h-px bg-adobe-500" />
                        <span className="eyebrow text-bone-200">Free · No-Obligation Estimate</span>
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
                        className="font-display font-black uppercase tracking-[-0.03em] leading-[0.9] text-bone-100 text-[13vw] md:text-[8vw] lg:text-[6.4rem]"
                    >
                        Get a free <span className="italic font-light text-adobe-500">estimate.</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.15 }}
                        className="mt-8 max-w-2xl font-body text-lg md:text-xl font-light text-bone-200 leading-relaxed"
                    >
                        Tell us about your project and our crew will follow up within 24 hours to schedule your free on-site walkthrough — interior, exterior, cabinets, stucco, and commercial work across all of New Mexico.
                    </motion.p>
                </div>
            </section>

            {/* Embedded lead form */}
            <section className="relative px-6 md:px-10 py-16 md:py-24">
                <div className="mx-auto max-w-[1600px]">
                    <div
                        data-testid="estimate-form-card"
                        className="relative w-full max-w-[640px] mx-auto overflow-hidden rounded-[22px] p-4 sm:p-6 shadow-[0_30px_90px_rgba(0,0,51,0.45)]"
                        style={{ backgroundColor: '#1C1A17', border: '1px solid rgba(210,180,140,0.35)' }}
                    >
                        <iframe
                            src="https://api.sentricai.io/widget/form/8GI9n7mrmdOakJV9qTtS"
                            style={{ width: '100%', height: '760px', border: 'none', borderRadius: '8px', background: 'transparent' }}
                            id="inline-8GI9n7mrmdOakJV9qTtS"
                            data-layout="{'id':'INLINE'}"
                            data-trigger-type="alwaysShow"
                            data-trigger-value=""
                            data-activation-type="alwaysActivated"
                            data-activation-value=""
                            data-deactivation-type="neverDeactivate"
                            data-deactivation-value=""
                            data-form-name="Website Lead Capture"
                            data-height="undefined"
                            data-layout-iframe-id="inline-8GI9n7mrmdOakJV9qTtS"
                            data-form-id="8GI9n7mrmdOakJV9qTtS"
                            title="Website Lead Capture"
                            data-testid="estimate-sentric-iframe"
                        />
                    </div>

                    {/* Legal links below the form */}
                    <div
                        data-testid="estimate-legal-links"
                        className="mx-auto max-w-[640px] mt-8 flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-3 text-center"
                    >
                        <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-bone-500">
                            <ShieldCheck size={13} strokeWidth={1.6} className="text-adobe-500" />
                            Your information is safe with us
                        </span>
                        <span className="hidden sm:block w-px h-4 bg-white/10" />
                        <div className="flex items-center gap-6">
                            <Link
                                to="/privacy"
                                data-testid="estimate-privacy-link"
                                className="link-underline font-mono text-[11px] tracking-[0.22em] uppercase text-bone-300 hover:text-adobe-500 transition-colors"
                            >
                                Privacy Policy
                            </Link>
                            <Link
                                to="/terms"
                                data-testid="estimate-terms-link"
                                className="link-underline font-mono text-[11px] tracking-[0.22em] uppercase text-bone-300 hover:text-adobe-500 transition-colors"
                            >
                                Terms &amp; Conditions
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Estimate;
